import { ChartParamsInput } from "src/pages/report/Interfaces";
import momentTimeZone from "moment-timezone";
import {
  CALENDAR_MODE_RANGE_TYPE,
  CHART_TYPE,
  WIDGET_TYPE
} from "@resources/Constants";
import {
  GraphData,
  WidgetDataType,
  WidgetDataTypeProps
} from "@components/report/Interfaces";
import {
  GetStoppedVehicleStatisticsResponse,
  VehicleData
} from "@queries/widgets/StoppedVehicleStatistics";
import { BaseTimeProps } from "src/pages/analytics/chart/ChartCard/Interfaces";
import moment from "moment";
import { theme as HVTheme } from "@repo/HVComponents";

export const getParams = ({
  widgetId,
  timeInterval,
  start,
  mode,
  currentTimeZone,
  end
}: ChartParamsInput) => {
  let params = {
    widgetId: widgetId as string,
    startDateTime: momentTimeZone.tz(start, currentTimeZone).toISOString(true),
    endDateTime: momentTimeZone.tz(end, currentTimeZone).toISOString(true),
    timeInterval
  };
  if (
    mode !== CALENDAR_MODE_RANGE_TYPE.LAST_24_HOURS &&
    mode !== CALENDAR_MODE_RANGE_TYPE.TODAY
  ) {
    params = {
      ...params,
      startDateTime: momentTimeZone
        .tz(start, currentTimeZone)
        .toISOString(true),
      endDateTime: momentTimeZone.tz(end, currentTimeZone).toISOString(true)
    };
  }

  if (mode === CALENDAR_MODE_RANGE_TYPE.TODAY) {
    params = {
      ...params,
      startDateTime: momentTimeZone.tz(start, currentTimeZone).toISOString(true)
    };
  }
  return params;
};

export function sumArrayFunction(sumArray: number[][], isSum = false) {
  if (isSum) {
    return sumArray.reduce(
      (acc: number[], array: number[]) => acc.map((sum, i) => sum + array[i]),
      new Array(sumArray[0].length).fill(0)
    );
  }

  return sumArray.reduce(
    (acc: number[], array: number[]) =>
      acc.map((sum, i) => sum + array[i] / sumArray.length),
    new Array(sumArray[0].length).fill(0)
  );
}

export const getChartDataCalculation = (
  graphData: GraphData,
  calculationType?: string,
  chartData?: number[][]
) => {
  const getChartArrays = (graphData: GraphData, chartData?: number[][]) => {
    if (chartData?.length) {
      return chartData;
    }
    const graphDataArray: WidgetDataType[] = Object.values(graphData);
    const [firstChartData, secondChartData, thirdChartData] = graphDataArray;
    const firstDataArray = firstChartData?.search?.chartData || [];
    const secondDataArray = secondChartData?.search?.chartData || [];
    const thirdDataArray = thirdChartData?.search?.chartData || [];
    const graphDataLength = graphDataArray ? graphDataArray.length : 0;

    if (graphDataLength === 3) {
      return [firstDataArray, secondDataArray, thirdDataArray];
    }

    if (graphDataLength === 2) {
      return [firstDataArray, secondDataArray];
    }
    if (graphDataLength === 1) {
      return [firstDataArray];
    }
    return [];
  };
  const chartArrays = getChartArrays(graphData, chartData);

  if (calculationType && ["SUM", "AVG"].includes(calculationType)) {
    const isSum = calculationType === "SUM";

    return [sumArrayFunction(chartArrays, isSum)];
  }

  if (chartArrays.length) return chartArrays;

  return [[]];
};
export const getVehicleStoppedData = (
  graphData: GetStoppedVehicleStatisticsResponse
) => {
  const chartData: number[] =
    graphData.search.chartData.car.map((_data, index) => {
      return (
        ((graphData.search.chartData.car &&
          graphData.search.chartData.car[index]) ||
          0) +
        ((graphData.search.chartData.bus &&
          graphData.search.chartData.bus[index]) ||
          0) +
        ((graphData.search.chartData.truck &&
          graphData.search.chartData.truck[index]) ||
          0) +
        ((graphData.search.chartData.motorcycle &&
          graphData.search.chartData.motorcycle[index]) ||
          0) +
        ((graphData.search.chartData.bicycle &&
          graphData.search.chartData.bicycle[index]) ||
          0)
      );
    }) || [];
  Object.assign(graphData.search, { chartData });
  return graphData;
};

const getStackBarGraphData = (data: WidgetDataTypeProps) => {
  const chartData = Object.values(data.search?.chartData);
  const barStackSeriesData: number[][] =
    chartData.map((items) => {
      return items;
    }) || [];
  return barStackSeriesData;
};

export const ensureCompleteVehicleData = (
  graphData: GetStoppedVehicleStatisticsResponse
) => {
  const arrayLength = Object.values(graphData.search.chartData)[0]?.length || 0;

  const defaultData: VehicleData = {
    car: new Array(arrayLength).fill(0),
    bus: new Array(arrayLength).fill(0),
    truck: new Array(arrayLength).fill(0),
    motorcycle: new Array(arrayLength).fill(0),
    bicycle: new Array(arrayLength).fill(0)
  };

  const chartData = { ...defaultData, ...graphData.search.chartData };
  Object.assign(graphData.search, { chartData });
  return graphData;
};

export const capitalizeFirstLetter = (string: string) => {
  if (!string) return "";
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export function sumOfArrayFunction(sumArrays: number[][][], isSum = false) {
  if (isSum) {
    return sumArrays[0].map(
      (_, i) =>
        sumArrays
          .map((subArray) => subArray[i])
          .reduce((acc, curr) => acc.map((sum, j) => sum + curr[j])),
      new Array(sumArrays[0].length).fill(0)
    );
  }
  return sumArrays[0].map(
    (_, i) =>
      sumArrays
        .map((subArray) => subArray[i])
        .reduce((acc, curr) => acc.map((value, j) => value + curr[j]))
        .map((sum) => sum / sumArrays.length),
    new Array(sumArrays[0].length).fill(0)
  );
}

export const getCalculationVehicleStopped = (vehicleData: VehicleData) => {
  const chartData: number[] =
    vehicleData.car.map((_data, index) => {
      return (
        ((vehicleData.car && vehicleData.car[index]) || 0) +
        ((vehicleData.bus && vehicleData.bus[index]) || 0) +
        ((vehicleData.truck && vehicleData.truck[index]) || 0) +
        ((vehicleData.motorcycle && vehicleData.motorcycle[index]) || 0) +
        ((vehicleData.bicycle && vehicleData.bicycle[index]) || 0)
      );
    }) || [];
  return chartData;
};

export const getStackBarGraphDataCalculation = (
  graphData: GraphData,
  calculationType?: string,
  chartStyle?: string
) => {
  const graphDataArray: WidgetDataTypeProps[] = Object.values(graphData);
  const [firstChartData, secondChartData, thirdChartData] = graphDataArray;
  const firstDataArray =
    (firstChartData && getStackBarGraphData(firstChartData)) || [];
  const secondDataArray =
    (secondChartData && getStackBarGraphData(secondChartData)) || [];
  const thirdDataArray =
    (thirdChartData && getStackBarGraphData(thirdChartData)) || [];
  const graphDataLength = graphDataArray ? graphDataArray.length : 0;
  if (calculationType && ["SUM", "AVG"].includes(calculationType)) {
    const isSum = calculationType === "SUM";
    const isAvg = calculationType === "AVG";
    if (
      chartStyle &&
      (chartStyle === CHART_TYPE.BAR_STACK ||
        chartStyle === CHART_TYPE.LINE_MULTILINE)
    ) {
      let sumArray: number[][][] = [];
      if (isSum) {
        sumArray =
          graphDataLength === 3
            ? [firstDataArray, secondDataArray, thirdDataArray]
            : [firstDataArray, secondDataArray];
      } else if (isAvg) {
        sumArray =
          graphDataLength === 3
            ? [firstDataArray, secondDataArray, thirdDataArray]
            : [firstDataArray, secondDataArray];
      }
      return sumOfArrayFunction(sumArray, isSum);
    }
    let sumAvgArray: number[][] = [];
    if (isSum) {
      sumAvgArray =
        graphDataLength === 3
          ? [
              getCalculationVehicleStopped(firstChartData.search.chartData),
              getCalculationVehicleStopped(secondChartData.search.chartData),
              getCalculationVehicleStopped(thirdChartData.search.chartData)
            ]
          : [
              getCalculationVehicleStopped(firstChartData.search.chartData),
              getCalculationVehicleStopped(secondChartData.search.chartData)
            ];
    } else if (isAvg) {
      sumAvgArray =
        graphDataLength === 3
          ? [
              getCalculationVehicleStopped(firstChartData.search.chartData),
              getCalculationVehicleStopped(secondChartData.search.chartData),
              getCalculationVehicleStopped(thirdChartData.search.chartData)
            ]
          : [
              getCalculationVehicleStopped(firstChartData.search.chartData),
              getCalculationVehicleStopped(secondChartData.search.chartData)
            ];
    }
    return [sumArrayFunction(sumAvgArray, isSum)];
  }

  if (graphDataLength === 3) {
    return [
      getCalculationVehicleStopped(firstChartData.search.chartData),
      getCalculationVehicleStopped(secondChartData.search.chartData),
      getCalculationVehicleStopped(thirdChartData.search.chartData)
    ];
  }

  if (graphDataLength === 2) {
    return [
      getCalculationVehicleStopped(firstChartData.search.chartData),
      getCalculationVehicleStopped(secondChartData.search.chartData)
    ];
  }

  return firstDataArray;
};

export const getLegendConfig = () => {
  return {
    type: "scroll",
    orient: "horizontal",
    bottom: -6,
    itemWidth: 8,
    itemHeight: 8,
    itemGap: 8,
    show: true,
    icon: "circle",
    selectedMode: false,
    textStyle: {
      overflow: "truncate",
      fontSize: "12px",
      padding: [0, 20, 0, 0]
    }
  };
};

export const getMappedWidgetType = (widgetType: string) => {
  switch (widgetType) {
    case WIDGET_TYPE.CHECKOUT_QUEUE:
      return "People queues";

    case WIDGET_TYPE.VEHICLE_QUEUE:
      return "Vehicle queues";

    case WIDGET_TYPE.SHOPPING_CART_CHECKOUT_QUEUE:
      return "Shopping cart queues";

    default:
      return "";
  }
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  const month = moment(date).format("MMM");
  const day = moment(date).format("DD");
  const year = moment(date).format("YYYY");
  return `${month}_${day}_${year}`;
};

export const csvDownloadFileName = (dateTime: BaseTimeProps, title: string) => {
  if (moment(dateTime.end).diff(dateTime.start, "day") === 0) {
    return `${title}_${formatDate(dateTime.start)}`;
  }
  return `${title}_${formatDate(dateTime.start)}-${formatDate(dateTime.end)}`;
};

const getCSSVariableValue = (variableName: string): string => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(variableName)
    .trim();
};

export const commonTooltipSettings = () => {
  return {
    backgroundColor: getCSSVariableValue("--hv-tooltip-background"),
    borderColor: getCSSVariableValue("--hv-border-default"),
    borderRadius: parseFloat(getCSSVariableValue("--hv-radius-sm")),
    textStyle: {
      fontSize: getCSSVariableValue("--hv-fontsize-body-xs"),
      fontFamily: HVTheme.fontFamily,
      color: getCSSVariableValue("--hv-text-on-brand-primary")
    },
    padding: [
      parseFloat(getCSSVariableValue("--hv-spacing-2")),
      parseFloat(getCSSVariableValue("--hv-spacing-3"))
    ]
  };
};
